import React, { useState } from 'react';
import { X } from 'lucide-react';
import { galleryImages } from '../data/mockData';
import LightEffects from './LightEffects';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);

  const categories = ['All', ...new Set(galleryImages.map(img => img.category))];
  const [activeCategory, setActiveCategory] = useState('All');

  const filteredImages = activeCategory === 'All'
    ? galleryImages
    : galleryImages.filter(img => img.category === activeCategory);

  return (
    <section id="gallery" className="relative bg-black py-24 md:py-32">
      {/* Light Effects */}
      <LightEffects variant="beams" />
      <LightEffects variant="radial" />
      
      <div className="container mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl md:text-6xl font-semibold text-white mb-6 tracking-tight">
            Our Work
          </h2>
          <div className="h-1 w-24 bg-blue-500 mx-auto mb-6"></div>
          <p className="text-xl text-white/70 max-w-3xl mx-auto leading-relaxed">
            Experience the transformative power of LFXPro Lights across concerts, festivals, architectural landmarks, and premium installations worldwide.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-4 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-3 text-base font-medium transition-all duration-300 ${
                activeCategory === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
              style={{ borderRadius: '0px' }}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredImages.map((image) => (
            <div
              key={image.id}
              className="group relative overflow-hidden cursor-pointer transition-all duration-500 hover:shadow-2xl hover:shadow-blue-500/30"
              onClick={() => setSelectedImage(image)}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={image.image}
                  alt={image.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6">
                <span className="text-xs text-blue-400 font-medium uppercase tracking-wider mb-2">
                  {image.category}
                </span>
                <h3 className="text-xl font-semibold text-white mb-2">
                  {image.title}
                </h3>
                <p className="text-sm text-white/70">
                  {image.description}
                </p>
              </div>

              {/* Blue accent line */}
              <div className="absolute top-0 left-0 w-0 h-1 bg-blue-500 transition-all duration-500 group-hover:w-full"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            className="absolute top-8 right-8 text-white hover:text-blue-500 transition-colors duration-300"
            onClick={() => setSelectedImage(null)}
          >
            <X size={40} />
          </button>
          
          <div className="max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="w-full h-auto max-h-[80vh] object-contain"
            />
            <div className="mt-6 text-center">
              <span className="text-sm text-blue-400 font-medium uppercase tracking-wider">
                {selectedImage.category}
              </span>
              <h3 className="text-2xl font-semibold text-white mt-2 mb-2">
                {selectedImage.title}
              </h3>
              <p className="text-base text-white/70">
                {selectedImage.description}
              </p>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;
